import type { GameEvent, Sanctuary, World } from './types';
import { civilFromEpochDay, dateLabel, dayOfYear, daysInMonth, daysInYear, epochDayFromCivil, epochDayOf } from './calendar';
import { ECONOMY, ROLE_NAMES, type Role, type SurveyKind } from './economy';
import { SURVEY_PROGRAMS, commissionedCount, type SurveyResult } from './monitoring';
import { reefOf } from './data/sites';
import { LIMIT_GROUPS, LIMIT_NAMES, LIMIT_OPTIONS, limitWords, patrolHoursAdded, type LimitGroup } from './management';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from './reef';
import { areaCounts, keptCounts, reefWideOf, type AreaCount, type AreaKind } from './areas';
import { STREAM } from './streams';
import { openingCampaign, stepCampaign, type Campaign } from './campaign';
import { CLEARED_T_PER_DAY, clearShore, shoreDebrisT, type ShoreReckoning } from './shoreDebris';

/**
 * The conservancy's operations (M238, the overhaul's brief, sections 2 to 5): its money, its staff, its one boat, and
 * the work they are sent on, surveys and the rules it proposes. The world is what the player investigates; this is how:
 * a survey is the monitoring's count taken again by the conservancy's own divers or cameras, its error the program's
 * scaled by the effort spent (`surveyCvScale`), so knowledge is bought, never handed over; a rule is the sanctuary's
 * proposal, consulted on with the island's communities and made by the State, in force only when the State has made it
 * (`management.ts` then acts on the fishing as before); patrols are the officers' hours it pays for. The figures are
 * the game's (`economy.ts`), apart from the science's.
 */

/** A State rule the sanctuary may propose: an island's Herbivore Protection Area set or lifted, or a fish group's limit. */
export type RuleChange =
  | { type: 'protect_herbivores'; island: ReefIsland | 'all'; protect: boolean }
  | { type: 'set_limit'; group: LimitGroup; limit: 'bag' | 'size'; option: number };

export interface StaffMember {
  id: number;
  name: string;
  role: Role;
  /** The last day paid for (an epoch day): pay runs from the day after, to the month's end or the day they leave. */
  paidTo: number;
}

/** Work under way: a survey at sea, a rule through its consultation and the State's rule-making, or a liaison setting up an island's volunteer monitoring (M277). */
export interface Job {
  id: number;
  kind: 'survey' | 'rule' | 'community';
  survey: SurveyKind | null;
  /** A survey's field days, or none. */
  days: number | null;
  rule: RuleChange | null;
  /** Who is on it (a rule's liaison only through its consultation). */
  staff: number[];
  startDay: number;
  /** The days its phases end, epoch days: a survey's one, a rule's consultation's and the State's. */
  phaseEnds: number[];
  cost: number;
  /** The island a survey was sent to, or none: the whole sanctuary, its sites over every island (M274). */
  island: ReefIsland | null;
  /** The day it was planned, an epoch day: its start, or earlier where it waited for the boat (M274). */
  plannedDay: number;
  /** The boat a survey is out on, 0 the *Kilo Kai*, 1 the second (M277); 0 for other work. */
  boat: number;
}

/** A thing built or bought (M277, the redesign's section 4): its kind, the day it was ordered, the day it is ready, and its cost. */
export interface Project {
  id: number;
  kind: 'boat';
  orderedDay: number;
  readyDay: number;
  cost: number;
}

/**
 * A survey planned, waiting for the boat (M274, the redesign's section 3): its kind, island and days, and the
 * biologists it takes, its crew named only when it leaves (whoever is free then); it leaves the day after the boat is
 * home, in the plan's order, when its crew is free and the funds hold its cost.
 */
export interface PlannedSurvey {
  id: number;
  kind: SurveyKind;
  island: ReefIsland | null;
  days: number;
  biologists: number;
  plannedDay: number;
}

/** A finished piece of work, as the reports tell it. */
export interface FinishedJob {
  id: number;
  kind: 'survey' | 'rule';
  endDay: number;
  title: string;
  /** The day it started (none where a save from before M274 did not keep it) and the days it waited for the boat before (a rule none), and a survey's island (M274: the month's report's trade-offs). */
  startDay: number | null;
  waited: number;
  island: ReefIsland | null;
  /** A shore cleanup's tonnes of nets and debris removed (M307); none on a survey or a rule, so a cleanup is told apart (`isCleanupHome`). */
  tonnes?: number;
}

/** A month's accounts, US dollars, income positive and spending negative, by line. */
export type Ledger = Record<LedgerLine, number>;
export const LEDGER_LINES = ['grant', 'research', 'tourism', 'salaries', 'boat', 'fuel', 'surveys', 'patrols', 'projects', 'capital'] as const;
export type LedgerLine = (typeof LEDGER_LINES)[number];
export const LEDGER_NAMES: Readonly<Record<LedgerLine, string>> = {
  grant: 'Operating grant', research: 'Research grants', tourism: 'Tourism partners', salaries: 'Salaries', boat: 'Boat upkeep',
  fuel: 'Fuel', surveys: 'Survey field costs', patrols: 'Patrols', projects: 'Community projects', capital: 'Boats and equipment',
};

export interface MonthReport {
  year: number;
  month: number;
  lines: Ledger;
  /** The funds at the month's end. */
  funds: number;
}

export interface Operations {
  /** US dollars; below zero the conservancy is in debt, and pays for no patrols. */
  funds: number;
  staff: StaffMember[];
  nextStaffId: number;
  /** How many have been hired after the opening crew, for the next one's name. */
  hired: number;
  jobs: Job[];
  nextJobId: number;
  /**
   * The latest finished work, newest last: the last `ECONOMY.monthsKept`, and all of the last 366 days however many
   * (M240's second review: the year's review reads its year's from it, and a year of short surveys had cut a rule made
   * early in it out).
   */
  finished: FinishedJob[];
  /** This month's accounts so far. */
  month: Ledger;
  /** The months closed, oldest first, `ECONOMY.monthsKept` at most. */
  months: MonthReport[];
  /** The reef's cover the tourism fees are paid at in full, percent: the monitoring's at the opening, or none where it tells none. */
  tourismCover: number | null;
  /** The surveys planned, waiting for the boat, in order (M274). */
  plan: PlannedSurvey[];
  /** The conservancy's counts of the management areas, newest last (M274, `areas.ts`). */
  areaCounts: AreaCount[];
  /**
   * What the conservancy chose when the reef's heat stress reached NOAA's first alert this episode: to assess the
   * bleaching, to keep its plan, or nothing yet; none again once the episode is over (M274, the redesign's section 6).
   */
  heatChoice: 'assess' | 'keep' | null;
  /** The boats the conservancy runs, one or two (M277). */
  boats: number;
  /** What it has built or bought, ordered and delivered, oldest first (M277). */
  projects: Project[];
  /** Each island's volunteer monitoring, in `REEF_ISLANDS` order: the day it began counting, or none (M277). */
  community: (number | null)[];
  /**
   * Each island's shore's last reckoning of its derelict gear, set by a cleanup (M307, `shoreDebris.ts`); an island
   * absent, or the whole field absent, holds the opening's on the world's first day, so a world no cleanup touched holds
   * nothing new (its fingerprint unmoved).
   */
  shores?: Partial<Record<ReefIsland, ShoreReckoning>>;
}

/** Dollars as the player reads them, "$12,500", "−$3,000" (no locale read in the core). */
export const dollars = (n: number): string => `${n < 0 ? '−' : ''}$${String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const emptyLedger = (): Ledger => Object.fromEntries(LEDGER_LINES.map((l) => [l, 0])) as Ledger;

/**
 * The reef's cover as the monitoring last told it on or before a day, percent, or none where it has told none: what the
 * visitors and the tour partners know of the reef is what its surveys publish, not the reef itself (JUDGED); and a save
 * holds it, so the operations replay from it (`replayedOperations`).
 */
export function knownCover(world: Pick<World, 'monitoring'>, day: number): number | null {
  const counts = world.monitoring.coral ?? [];
  for (let i = counts.length - 1; i >= 0; i--) if ((counts[i] as { day: number }).day <= day) return (counts[i] as { value: number }).value;
  return null;
}

/** A new conservancy's operations on its first day: its opening funds and crew, none of its work begun. */
export function openingOperations(world: Pick<World, 'monitoring' | 'startEpochDay' | 'day'>): Operations {
  const today = epochDayOf(world);
  return {
    funds: ECONOMY.openingFunds,
    staff: ECONOMY.openingCrew.map((c, i) => ({ id: i + 1, name: c.name, role: c.role, paidTo: today - 1 })),
    nextStaffId: ECONOMY.openingCrew.length + 1,
    hired: 0,
    jobs: [],
    nextJobId: 1,
    finished: [],
    month: emptyLedger(),
    months: [],
    tourismCover: knownCover(world, today),
    plan: [],
    areaCounts: [],
    heatChoice: null,
    boats: 1,
    projects: [],
    community: REEF_ISLANDS.map(() => null),
  };
}

/** NOAA's divers' sites at Maui Nui in its latest survey (MEASURED from the archived strata, `MAUI_NUI_REEF`): what a commissioned reef survey's effort is set against. */
export function noaaReefSites(s: Sanctuary | undefined): number | null {
  const survey = (s ? reefOf(s) : null)?.surveys.at(-1);
  return survey ? survey.strata.reduce((n, st) => n + st.sites, 0) : null;
}

/** NOAA's bottomfish survey's days in 2024, 10 August to 24 November (Richards 2025, p. 3): a commissioned camera survey's set against them. */
export const BFISH_DAYS = epochDayFromCivil(2024, 11, 24) - epochDayFromCivil(2024, 8, 10) + 1;

/** A survey's dive teams: its biologists in pairs, each a buddy's (on the slope the camera's operator alone). */
const teamsOf = (kind: SurveyKind, biologists: number): number => (kind === 'slopeCamera' ? Math.min(1, biologists) : Math.floor(biologists / 2));

/**
 * How a commissioned survey's error stands to the program's: a reef survey's sites against NOAA's (the error of a mean
 * falling as the square root of its sites), a camera survey's days against NOAA's season's (JUDGED: its drops a day
 * taken as the survey's; NOAA's covers every main island, so this is kind to the conservancy's).
 */
export function surveyCvScale(kind: SurveyKind, days: number, biologists: number, s: Sanctuary | undefined): number | null {
  const teams = teamsOf(kind, biologists);
  if (teams <= 0 || days <= 0) return null;
  if (kind === 'slopeCamera') return Math.sqrt(BFISH_DAYS / days);
  const noaa = noaaReefSites(s);
  return noaa === null ? null : Math.sqrt(noaa / (days * teams * ECONOMY.reefSitesPerDay));
}

/** A survey's cost, US dollars: its fuel and field costs every day at sea. */
export const surveyCost = (kind: SurveyKind, days: number): number => days * (ECONOMY.boat.fuelPerDay + ECONOMY.fieldCostPerDay[kind]);

/** The survey kinds a sanctuary's habitats hold: the reef's two where it has a reef, the slope's where it has the Deep 7. */
export function surveyKindsAt(s: Sanctuary | undefined): SurveyKind[] {
  if (!s) return [];
  return [...(s.coral && s.fish ? ['reefFish', 'reefBenthic', 'reefBleaching'] as const : []), ...(s.slope ? ['slopeCamera'] as const : [])];
}

export const SURVEY_NAMES: Readonly<Record<SurveyKind, string>> = { reefFish: 'Reef fish census', reefBenthic: 'Reef benthic survey', reefBleaching: 'Bleaching assessment', slopeCamera: 'Deep 7 camera survey', shoreCleanup: 'Shore cleanup' };

/**
 * Whether a finished trip was a shore cleanup (M307), not a survey: the boat's mission, which counts nothing, so no
 * campaign objective, review line, goal or report that counts surveys counts it.
 */
export const isCleanupHome = (f: FinishedJob): boolean => f.kind === 'survey' && f.tonnes !== undefined;
/** A survey home: a finished trip of the boat's that counted (a cleanup not, M307). */
export const isSurveyHome = (f: FinishedJob): boolean => f.kind === 'survey' && f.tonnes === undefined;

/** What each reef survey counts of the areas it covers (M274): the fish census the herbivores, the benthic survey the coral, the assessment its bleaching. */
export const AREA_KIND: Readonly<Partial<Record<SurveyKind, AreaKind>>> = { reefFish: 'herbivores', reefBenthic: 'cover', reefBleaching: 'bleached' };

/** Whether a survey kind may be sent to one island: the reef's, and a shore cleanup (M307) (the slope's cameras are set where NOAA's are, over the whole sanctuary). */
export const islandSurvey = (kind: SurveyKind): boolean => AREA_KIND[kind] !== undefined || kind === 'shoreCleanup';

/** Whether a staff member is on work today: a survey's crew to its end, a rule's liaison to its consultation's. */
export function busyOn(ops: Operations, staffId: number): Job | null {
  return ops.jobs.find((j) => j.staff.includes(staffId)) ?? null;
}

/** The job the first boat at sea is out on, or none. */
export const boatJob = (ops: Operations): Job | null => ops.jobs.find((j) => j.kind === 'survey') ?? null;

/** The surveys at sea, one a boat (M277). */
export const atSea = (ops: Operations): Job[] => ops.jobs.filter((j) => j.kind === 'survey');

/** A boat home and free, its number, or none (M277: the *Kilo Kai* first). */
export function freeBoat(ops: Operations): number | null {
  for (let b = 0; b < ops.boats; b++) if (!ops.jobs.some((j) => j.kind === 'survey' && j.boat === b)) return b;
  return null;
}

/** A boat's name: the *Kilo Kai*, or the second (M277). */
export const boatName = (b: number): string => (b === 0 ? ECONOMY.boat.name : ECONOMY.secondBoat.name);

/** The most of a role the conservancy may employ: a captain for each boat it runs or has on order (M277), else the economy's. */
export function mostOf(ops: Operations, role: Role): number {
  return role === 'captain' ? ops.boats + (ops.boats < 2 && ops.projects.some((p) => p.kind === 'boat') ? 1 : 0) : ECONOMY.mostOf[role];
}

/** A rule's words, as the player reads it. */
export function ruleWords(r: RuleChange): string {
  if (r.type === 'protect_herbivores') return `${r.protect ? 'A Herbivore Protection Area on' : 'Lifting the Herbivore Protection Area on'} ${r.island === 'all' ? 'all of Maui Nui\'s reef' : ISLAND_NAMES[r.island]}`;
  return r.option === 0 ? `Hawaiʻi's own ${r.limit === 'bag' ? 'bag limit' : 'minimum size'} for the ${LIMIT_NAMES[r.group]}`
    : `A ${r.limit === 'bag' ? 'bag limit' : 'minimum size'} for the ${LIMIT_NAMES[r.group]}: ${limitWords(r.group, r.limit, r.option)}`;
}

/** Whether a rule would change what the State's rules are now. */
function ruleChanges(s: Sanctuary, r: RuleChange): boolean {
  const m = s.management;
  if (!m) return false;
  if (r.type === 'protect_herbivores') return r.island === 'all' ? m.herbivoreProtection.some((p) => p !== r.protect) : m.herbivoreProtection[REEF_ISLANDS.indexOf(r.island)] !== r.protect;
  return m.limits[r.group][r.limit] !== r.option;
}

/** Whether two rules set the same thing (an island's protection, a group's limit): one proposal at a time for it, and no direct change beside it. */
export const sameTarget = (a: RuleChange, b: RuleChange): boolean => (a.type === 'protect_herbivores' && b.type === 'protect_herbivores' && (a.island === b.island || a.island === 'all' || b.island === 'all'))
  || (a.type === 'set_limit' && b.type === 'set_limit' && a.group === b.group && a.limit === b.limit);

/** A rule as a command gives it, checked, or why not. */
export function checkedRule(r: unknown): RuleChange | string {
  const x = r as Partial<RuleChange> | null;
  if (!x || typeof x !== 'object') return 'no such rule';
  if (x.type === 'protect_herbivores') {
    return ((REEF_ISLANDS as readonly string[]).includes(x.island as string) || x.island === 'all') && typeof x.protect === 'boolean' ? { type: x.type, island: x.island as ReefIsland | 'all', protect: x.protect } : 'no such island or choice';
  }
  if (x.type === 'set_limit') {
    const g = x.group as LimitGroup;
    if (!(LIMIT_GROUPS as readonly string[]).includes(g) || (x.limit !== 'bag' && x.limit !== 'size')) return 'no such fish group or limit';
    const options = x.limit === 'bag' ? LIMIT_OPTIONS[g].bags : LIMIT_OPTIONS[g].sizes;
    return Number.isInteger(x.option) && (x.option as number) >= 0 && (x.option as number) < options.length ? { type: x.type, group: g, limit: x.limit, option: x.option as number } : 'no such choice';
  }
  return 'no such rule';
}

/** Whether a survey can be planned: its kind, island and days the sanctuary's; or why not. */
function surveyCheck(world: World, kind: SurveyKind, days: number, island: ReefIsland | null): string | null {
  // A shore cleanup (M307) where the sanctuary has a reef's islands, to one of them: a shore, never the whole sanctuary.
  const s = world.sanctuaries[0];
  if (kind === 'shoreCleanup' ? !(s?.coral && s.fish) : !surveyKindsAt(s).includes(kind)) return 'this sanctuary has no such survey';
  if (kind === 'shoreCleanup' && island === null) return 'a shore cleanup goes to one island';
  if (!ECONOMY.surveyDays.includes(days)) return 'no such length of survey';
  // A bleaching assessment's rapid count, its days at most the economy's, on every path to sea (review 32's: the survey
  // page sent one for 20 days).
  if (kind === 'reefBleaching' && days > ECONOMY.assessmentDays) return `a bleaching assessment takes ${ECONOMY.assessmentDays} days at most`;
  if (island !== null && !(REEF_ISLANDS.includes(island) && islandSurvey(kind))) return 'no such island for this survey';
  return null;
}

/** Send the boat on a survey, its crew named, to an island or the whole sanctuary; or why it cannot go. */
export function startSurvey(world: World, kind: SurveyKind, days: number, crew: readonly number[], island: ReefIsland | null = null, planned?: { id: number; day: number }): Job | string {
  const ops = world.operations;
  const why = surveyCheck(world, kind, days, island);
  if (why) return why;
  const boat = freeBoat(ops);
  if (boat === null) return ops.boats > 1 ? 'both boats are already at sea' : `the ${ECONOMY.boat.name} is already at sea`;
  // The plan's surveys first, in turn (review M274's: one sent from the survey page went ahead of them).
  if (!planned && ops.plan.length > 0) return 'surveys are planned before it: plan it, and it goes in its turn';
  const members = crew.map((id) => ops.staff.find((m) => m.id === id));
  if (members.some((m) => !m) || new Set(crew).size !== crew.length) return 'no such crew';
  if (members.some((m) => busyOn(ops, (m as StaffMember).id))) return 'someone named is already at work';
  const count = (role: Role): number => members.filter((m) => m?.role === role).length;
  if (count('captain') !== 1) return 'a survey needs its captain, and one only';
  if (count('liaison') > 0) return 'the liaison does not go to sea';
  const biologists = count('biologist');
  if (kind === 'slopeCamera' ? biologists < 1 : biologists < 2 || biologists % 2 !== 0) return kind === 'slopeCamera' ? 'a camera survey needs a diver' : 'divers go in pairs: two, or four';
  const cost = surveyCost(kind, days);
  if (ops.funds < cost) return `it costs ${dollars(cost)}, more than the conservancy has`;
  const today = epochDayOf(world);
  const job: Job = { id: planned?.id ?? ops.nextJobId++, kind: 'survey', survey: kind, days, rule: null, staff: [...crew], startDay: today, phaseEnds: [today + days], cost, island, plannedDay: planned?.day ?? today, boat };
  spend(ops, 'fuel', days * ECONOMY.boat.fuelPerDay);
  spend(ops, 'surveys', days * ECONOMY.fieldCostPerDay[kind]);
  ops.jobs.push(job);
  return job;
}

/**
 * A shore cleanup kept out longer (M331, `docs/ONE_REEF_ROADMAP.md`'s O6: a decision inside the trip, before it ends):
 * its days and its cost grown by a trip's days (`ECONOMY.surveyDays`' first), its crew and its boat with it, the trips
 * planned after it waiting the more; the shore cleared at the same rate a day (`CLEARED_T_PER_DAY`, KIRC's grant years on Kahoʻolawe as NOAA's Marine Debris Program reports them), to the
 * plan's longest trip in all. Or why not.
 */
/** The tonnes the cleanups at sea on a shore will clear, every boat's (review 34: a second boat's on the same shore). */
export const clearingAtSea = (ops: Operations, island: ReefIsland): number =>
  ops.jobs.filter((j) => j.kind === 'survey' && j.survey === 'shoreCleanup' && j.island === island).reduce((n, j) => n + CLEARED_T_PER_DAY * (j.days ?? 0), 0);

export function extendTrip(world: World, days: number): Job | string {
  const ops = world.operations;
  const job = ops.jobs.find((j) => j.kind === 'survey' && j.survey === 'shoreCleanup');
  if (!job) return 'no cleanup is at sea';
  if (days !== ECONOMY.surveyDays[0]) return 'there is no such stay';
  if ((job.days ?? 0) + days > (ECONOMY.surveyDays.at(-1) as number)) return `a trip is ${ECONOMY.surveyDays.at(-1)} days at most`;
  // Its shore holding a stay's clearing past this trip's (its review: the core had sold days that clear nothing).
  if (job.island === null || shoreDebrisT(world, job.island) - clearingAtSea(ops, job.island) < CLEARED_T_PER_DAY * days) return 'its shore holds too little more to clear';
  const cost = surveyCost('shoreCleanup', days);
  if (ops.funds < cost) return `its ${dollars(cost)} is more than the conservancy has`;
  job.days = (job.days ?? 0) + days;
  job.phaseEnds = [(job.phaseEnds[0] as number) + days];
  job.cost += cost;
  spend(ops, 'fuel', days * ECONOMY.boat.fuelPerDay);
  spend(ops, 'surveys', days * ECONOMY.fieldCostPerDay.shoreCleanup);
  return job;
}

/** A liaison on no work today, the first of them; none where every one is busy or none is employed (one reading for the core and the pages, review 33, M298). */
export const freeLiaison = (ops: Operations): Operations['staff'][number] | undefined => ops.staff.find((m) => m.role === 'liaison' && !busyOn(ops, m.id));

/** Whether volunteers count an island's grazing fish, or a liaison trains them there (review 33: five copies of the test). */
export const volunteersOn = (ops: Operations, island: ReefIsland): boolean => ops.community[REEF_ISLANDS.indexOf(island)] !== null || ops.jobs.some((j) => j.kind === 'community' && (j.island === island || j.island === null));
/** An island's Herbivore Protection Area under way, its consultation or the State's step; none where none is (one reading for the pages, review 33, M298: four copies). */
/** Any proposal on the grazing fish's protection under way, one island's (an older save's) or all of Maui Nui's, to make it or to lift it (M327's review: the core refuses another while one is, `sameTarget`). */
export const anyProtectionJob = (ops: Operations): Job | undefined => ops.jobs.find((j) => j.kind === 'rule' && j.rule?.type === 'protect_herbivores');
export const protectionJob = (ops: Operations, island: ReefIsland | 'all'): Job | undefined => ops.jobs.find((j) => j.kind === 'rule' && j.rule?.type === 'protect_herbivores' && (j.rule.island === island || j.rule.island === 'all') && j.rule.protect);

/** Propose a rule to the State: the liaison's consultation, then its rule-making; or why not. */
/**
 * A rule's consultation's cost (M327, one reef): a protected area for all of Maui Nui consults each island's communities,
 * each island's consultation's cost (`ECONOMY.rule`), its liaison holding their meetings in the same months. GAME CHOICE:
 * one rule for the reef the player manages as one, its consultations together; four times one island's, as many
 * communities' meetings, the months one island's (no source gives a consultation's length by its communities' number).
 */
export const consultationCost = (rule: RuleChange): number => ECONOMY.rule.consultationCost * (rule.type === 'protect_herbivores' && rule.island === 'all' ? REEF_ISLANDS.length : 1);

export function proposeRule(world: World, rule: RuleChange): Job | string {
  const ops = world.operations;
  const s = world.sanctuaries[0];
  if (!s?.management) return 'this sanctuary has no reef fish to make rules for';
  if (!ruleChanges(s, rule)) return 'that rule is already in force';
  if (ops.jobs.some((j) => j.rule && sameTarget(j.rule, rule))) return 'a proposal for it is already under way';
  const liaison = freeLiaison(ops);
  if (!liaison) return 'no liaison is free to lead its consultation';
  const cost = consultationCost(rule);
  if (ops.funds < cost) return `its consultation costs ${dollars(cost)}, more than the conservancy has`;
  const today = epochDayOf(world);
  const consulted = today + ECONOMY.rule.consultationDays;
  const job: Job = { id: ops.nextJobId++, kind: 'rule', survey: null, days: null, rule, staff: [liaison.id], startDay: today, phaseEnds: [consulted, consulted + ECONOMY.rule.stateDays], cost, island: null, plannedDay: today, boat: 0 };
  spend(ops, 'projects', cost);
  ops.jobs.push(job);
  return job;
}

/** The crew a survey of a kind and its biologists takes from those free today, the captain first; none where they are not free. */
function freeCrew(ops: Operations, kind: SurveyKind, biologists: number): number[] | null {
  const free = (role: Role): StaffMember[] => ops.staff.filter((m) => m.role === role && !busyOn(ops, m.id));
  const captain = free('captain').slice(0, 1);
  const divers = free('biologist').slice(0, biologists);
  return captain.length === 1 && divers.length === biologists ? [...captain, ...divers].map((m) => m.id) : null;
}

/** Whether a planned survey's biologists are a crew its kind takes: one or more on the cameras, divers in pairs. */
const biologistsFit = (kind: SurveyKind, n: number): boolean => Number.isInteger(n) && n <= ECONOMY.mostOf.biologist && (kind === 'slopeCamera' ? n >= 1 : n >= 2 && n % 2 === 0);

/**
 * Plan a survey (M274): sent at once where the boat is home, nothing waits before it and its crew is free; else put in
 * the boat's plan, last, or first where it is wanted first (a bleaching assessment in a heatwave), to leave when the
 * boat is home; or why not.
 */
/** Whether a survey planned now leaves at once: a boat home, nothing waiting before it, its crew free and its cost in hand (M277: told on its button as `planSurvey` does it). */
export function sendsNow(world: World, kind: SurveyKind, days: number, biologists: number): boolean {
  const ops = world.operations;
  return freeBoat(ops) !== null && ops.plan.length === 0 && freeCrew(ops, kind, biologists) !== null && ops.funds >= surveyCost(kind, days);
}

export function planSurvey(world: World, p: { kind: SurveyKind; island: ReefIsland | null; days: number; biologists: number; first?: boolean }): Job | PlannedSurvey | string {
  const ops = world.operations;
  const why = surveyCheck(world, p.kind, p.days, p.island);
  if (why) return why;
  if (!biologistsFit(p.kind, p.biologists)) return p.kind === 'slopeCamera' ? 'a camera survey needs a diver' : 'divers go in pairs: two, or four';
  if (ops.staff.filter((m) => m.role === 'biologist').length < p.biologists) return 'the conservancy has not so many divers';
  if (!ops.staff.some((m) => m.role === 'captain')) return 'the conservancy has no captain';
  const today = epochDayOf(world);
  // A bleaching assessment planned in a heatwave is the heat's choice made (`keepPlanInHeat` the other), once it is
  // planned (review M274's: set before a full plan refused it, the choice was gone and nothing planned).
  const choosing = p.kind === 'reefBleaching' && ops.heatChoice === null && (world.sanctuaries[0]?.heatStress.peakDhw ?? 0) >= HEAT_ALERT_DHW;
  const crew = freeCrew(ops, p.kind, p.biologists);
  let made: Job | PlannedSurvey | string;
  // Sent at once where the boat is home, nothing waits and its crew and cost are there; else it waits its turn.
  if (sendsNow(world, p.kind, p.days, p.biologists) && crew) made = startSurvey(world, p.kind, p.days, crew, p.island);
  else if (ops.plan.length >= ECONOMY.planLength) made = `the plan holds ${ECONOMY.planLength} surveys already`;
  else {
    const planned: PlannedSurvey = { id: ops.nextJobId++, kind: p.kind, island: p.island, days: p.days, biologists: p.biologists, plannedDay: today };
    ops.plan = p.first ? [planned, ...ops.plan] : [...ops.plan, planned];
    made = planned;
  }
  if (choosing && typeof made !== 'string') ops.heatChoice = 'assess';
  return made;
}

/** Take a survey out of the boat's plan, or put it first; or why not. */
export function replan(world: World, id: number, to: 'cancel' | 'first'): PlannedSurvey | string {
  const ops = world.operations;
  const p = ops.plan.find((x) => x.id === id);
  if (!p) return 'no such survey planned';
  ops.plan = to === 'cancel' ? ops.plan.filter((x) => x !== p) : [p, ...ops.plan.filter((x) => x !== p)];
  return p;
}

/**
 * The days a planned survey is expected to leave and come home, epoch days: in turn after the one at sea and those
 * before it in the plan, each leaving the day after the last is home (none earlier than tomorrow), as `stepOperations`
 * sends them, each on a boat, a captain and its divers free (M277; review 32's); funds taken as holding.
 */
export function planDays(world: World, extra?: { days: number; first: boolean; biologists?: number }): { id: number; leaves: number | null; home: number | null }[] {
  const ops = world.operations;
  const today = epochDayOf(world);
  // Each boat's, captain's and biologist's first free day, those at sea the day after they are home (review 32's: the
  // boats alone were followed, so a survey waiting on divers out on the other boat was dated a survey early).
  const freeOf = (id: number): number => { const j = atSea(ops).find((x) => x.staff.includes(id)); return j ? (j.phaseEnds[0] as number) + 1 : today + 1; };
  const boats = Array.from({ length: ops.boats }, (_, b) => { const j = atSea(ops).find((x) => x.boat === b); return j ? (j.phaseEnds[0] as number) + 1 : today + 1; });
  const captains = ops.staff.filter((m) => m.role === 'captain').map((m) => freeOf(m.id));
  const divers = ops.staff.filter((m) => m.role === 'biologist').map((m) => freeOf(m.id));
  // A survey not yet planned, last or first, its days told with the rest's (id -1: an island card's button).
  const next = extra ? [{ id: -1, days: extra.days, biologists: extra.biologists ?? 2 }] : [];
  const order = extra?.first ? [...next, ...ops.plan] : [...ops.plan, ...next];
  // In turn, as `stepOperations` sends them: none before the one ahead of it; one no crew the conservancy employs can
  // sail holds every one behind it (review 32's: its dates printed as NaN).
  let after = today + 1;
  return order.map((p) => {
    const need = p.biologists;
    if (!Number.isFinite(after) || boats.length === 0 || captains.length === 0 || divers.length < need) {
      after = Infinity;
      return { id: p.id, leaves: null, home: null };
    }
    for (const xs of [boats, captains, divers]) xs.sort((a, b) => a - b);
    const leaves = Math.max(after, boats[0] as number, captains[0] as number, need > 0 ? (divers[need - 1] as number) : after);
    const home = leaves + p.days;
    boats[0] = home + 1;
    captains[0] = home + 1;
    for (let k = 0; k < need; k++) divers[k] = home + 1;
    after = leaves;
    return { id: p.id, leaves, home };
  });
}

/** Why the boat's next planned survey has not left though the boat is home, or none: its crew or its cost (M274). */
export function planWait(world: World): string | null {
  const ops = world.operations;
  const next = ops.plan[0];
  if (!next || freeBoat(ops) === null) return null;
  // A crew the conservancy does not employ, told so with the way out (review 32's: told "not free" for good).
  const employed = (role: Role): number => ops.staff.filter((m) => m.role === role).length;
  if (employed('captain') === 0 || employed('biologist') < next.biologists) return `it takes a captain and ${next.biologists} ${next.biologists === 1 ? 'biologist' : 'biologists'}, and the conservancy employs ${employed('captain')} and ${employed('biologist')}: hire, or take it out of the plan`;
  if (!freeCrew(ops, next.kind, next.biologists)) return `its crew is not free: it takes the captain and ${next.biologists} ${next.biologists === 1 ? 'biologist' : 'biologists'}`;
  const cost = surveyCost(next.kind, next.days);
  return ops.funds < cost ? `it costs ${dollars(cost)}, more than the conservancy has` : null;
}

/** Order the second boat (M277): once the first operating grant has come, its price in hand, no boat already on order; or why not. */
export function orderBoat(world: World): Project | string {
  const ops = world.operations;
  if (ops.boats >= 2 || ops.projects.some((p) => p.kind === 'boat')) return `the ${ECONOMY.secondBoat.name} is already ordered`;
  if (!ops.months.some((m) => m.lines.grant > 0) && ops.month.grant <= 0) return 'a boat is bought from the year\'s operating grant, and the first has not come';
  if (ops.funds < ECONOMY.secondBoat.price) return `it costs ${dollars(ECONOMY.secondBoat.price)}, more than the conservancy has`;
  const today = epochDayOf(world);
  const p: Project = { id: ops.nextJobId++, kind: 'boat', orderedDay: today, readyDay: today + ECONOMY.secondBoat.deliveryDays, cost: ECONOMY.secondBoat.price };
  spend(ops, 'capital', p.cost);
  ops.projects.push(p);
  return p;
}

/** Set up volunteer monitoring of an island (M277): a free liaison's month with its community; or why not. */
export function startCommunity(world: World, island: ReefIsland | 'all'): Job | string {
  const ops = world.operations;
  // All of Maui Nui (M327, one reef): each island whose volunteers do not yet count, set up in the one month by its
  // liaison, each its own program and its year's cost (GAME CHOICE: the reef the player manages as one; the month one
  // island's, ECONOMY.community).
  if (island === 'all') {
    if (!world.sanctuaries[0]?.fish) return 'no such island';
    if (REEF_ISLANDS.every((each) => volunteersOn(ops, each))) return 'its volunteers are already at work';
    // One setting up at a time (M327's review: an island's under way beside the reef's made a save that would not load).
    if (ops.jobs.some((j) => j.kind === 'community')) return 'volunteers are already being set up';
  } else if (REEF_ISLANDS.indexOf(island) < 0 || !world.sanctuaries[0]?.fish) return 'no such island';
  else if (volunteersOn(ops, island)) return 'its volunteers are already at work';
  const liaison = freeLiaison(ops);
  if (!liaison) return 'no liaison is free to set it up';
  const today = epochDayOf(world);
  const job: Job = { id: ops.nextJobId++, kind: 'community', survey: null, days: null, rule: null, staff: [liaison.id], startDay: today, phaseEnds: [today + ECONOMY.community.setupDays], cost: 0, island: island === 'all' ? null : island, plannedDay: today, boat: 0 };
  ops.jobs.push(job);
  return job;
}

/** End an island's volunteer monitoring (M277); or why not. */
export function stopCommunity(world: World, island: ReefIsland): string | null {
  const ops = world.operations;
  const i = REEF_ISLANDS.indexOf(island);
  if (i < 0 || ops.community[i] === null) return 'no volunteers count there';
  ops.community[i] = null;
  return null;
}

/** Hire one more of a role; or why not. */
export function hire(world: World, role: Role): StaffMember | string {
  const ops = world.operations;
  if (!Object.hasOwn(ECONOMY.mostOf, role)) return 'no such role';
  if (ops.staff.filter((m) => m.role === role).length >= mostOf(ops, role)) return `the conservancy employs as many in the role of ${ROLE_NAMES[role].toLowerCase()} as it can use`;
  if (ops.funds < ECONOMY.monthlyPay[role]) return 'the conservancy cannot pay another salary';
  const name = ECONOMY.hireNames[ops.hired % ECONOMY.hireNames.length] as string;
  const m: StaffMember = { id: ops.nextStaffId++, name, role, paidTo: epochDayOf(world) - 1 };
  ops.hired++;
  ops.staff.push(m);
  return m;
}

/** Let a staff member go, paid to today; or why not. */
export function letGo(world: World, staffId: number): StaffMember | string {
  const ops = world.operations;
  const m = ops.staff.find((x) => x.id === staffId);
  if (!m) return 'no such staff member';
  if (busyOn(ops, staffId)) return `${m.name} is at work`;
  pay(ops, m, epochDayOf(world));
  ops.staff = ops.staff.filter((x) => x.id !== staffId);
  return m;
}

function spend(ops: Operations, line: LedgerLine, dollars: number): void {
  ops.funds -= dollars;
  ops.month[line] -= dollars;
}

export function earn(ops: Operations, line: LedgerLine, dollars: number): void {
  ops.funds += dollars;
  ops.month[line] += dollars;
}

/** A staff member's pay from the day after the last paid to a day, at their role's month's pay over the month's days. */
function pay(ops: Operations, m: StaffMember, to: number): void {
  let owed = 0;
  for (let d = m.paidTo + 1; d <= to; d++) {
    const c = civilFromEpochDay(d);
    owed += ECONOMY.monthlyPay[m.role] / daysInMonth(c.year, c.month);
  }
  m.paidTo = to;
  if (owed > 0) spend(ops, 'salaries', Math.round(owed));
}

/** The patrols' cost over some days, US dollars: the officers' hours each island's level adds, those days' share of the year's. */
export function patrolCost(s: Sanctuary | undefined, days: number): number {
  const m = s?.management;
  if (!m) return 0;
  const hours = REEF_ISLANDS.reduce((h, island, i) => h + patrolHoursAdded(island, m.patrols[i] ?? 0), 0);
  // The hours are a year's (by definition 365 days, the calendar's common year).
  return Math.round((hours * days / 365) * ECONOMY.officerHour);
}

/** The day's operations, after the day's animals and the monitoring's counts: work finished, the grant, the month's accounts. */
export function stepOperations(world: World, events: GameEvent[]): void {
  const ops = world.operations;
  const today = epochDayOf(world);
  const civil = civilFromEpochDay(today);
  const s = world.sanctuaries[0];
  // The plan's next surveys leave, one for each boat home since yesterday or before (so a survey home today is followed
  // tomorrow), its crew free and its cost in hand; else the next waits, and the plan tells why (`planWait`).
  for (let next = ops.plan[0]; next && freeBoat(ops) !== null; next = ops.plan[0]) {
    const crew = freeCrew(ops, next.kind, next.biologists);
    if (!crew || ops.funds < surveyCost(next.kind, next.days)) break;
    const job = startSurvey(world, next.kind, next.days, crew, next.island, { id: next.id, day: next.plannedDay });
    if (typeof job === 'string') break;
    ops.plan = ops.plan.slice(1);
    events.push({ day: world.day, severity: 'info', title: `The ${boatName(job.boat)} leaves on a ${SURVEY_NAMES[next.kind].toLowerCase()}`, detail: `${next.island ? `To ${ISLAND_NAMES[next.island]}` : 'Over the whole sanctuary'}, ${next.days} days, as planned on ${dateLabel(next.plannedDay)}.` });
  }
  for (const job of [...ops.jobs]) finishPhase(world, job, today, events);
  // A boat ordered comes on its day (M277).
  for (const p of ops.projects) {
    if (p.kind !== 'boat' || p.readyDay !== today) continue;
    ops.boats = 2;
    events.push({ day: world.day, severity: 'good', title: `The ${ECONOMY.secondBoat.name} is in the water`, detail: 'A second boat: two surveys can be at sea at once, each with its own captain and crew.' });
  }
  // Each island's volunteers paid by the day, and their month's transects counted on its last (M277).
  // From the day after their setup, the day they count from; a year's cost over the year's days, each day the share
  // that brings the year to it exactly (review M277's: $27 a day was $9,855 a year).
  const volunteers = ops.community.filter((d) => d !== null && d < today).length;
  const k = dayOfYear(today);
  const yearDays = daysInYear(civil.year);
  const cost = volunteers * ECONOMY.community.costPerYear;
  if (volunteers > 0) spend(ops, 'projects', Math.round((cost * k) / yearDays) - Math.round((cost * (k - 1)) / yearDays));
  if (civil.day === daysInMonth(civil.year, civil.month)) {
    const counted = REEF_ISLANDS.flatMap((island, i) => (ops.community[i] !== null && (ops.community[i] as number) < today
      ? areaCounts(world, 'herbivores', [island], ECONOMY.community.sitesPerMonth, STREAM.operations, today).map((c) => ({ ...c, by: 'community' as const }))
      : []));
    ops.areaCounts = keptCounts([...ops.areaCounts, ...counted]);
  }
  // The heat's choice asked again in the next episode (M274).
  if (ops.heatChoice !== null && (s?.heatStress.peakDhw ?? 0) < HEAT_ALERT_DHW) ops.heatChoice = null;
  // The officers' hours bought, paid by the day at the day's levels (review M238's: billed at the month's last day's,
  // a level set and lifted within a month went free).
  spend(ops, 'patrols', patrolCost(s, 1));
  // A patrol bought for a term ends on its last day, paid for it: Hawaiʻi's officers alone again from tomorrow (M274).
  const m = s?.management;
  if (m) {
    m.patrolUntil.forEach((until, i) => {
      if (until === null || until !== today) return;
      m.patrols[i] = 0;
      m.patrolUntil[i] = null;
      events.push({ day: world.day, severity: 'info', title: `The patrol on ${ISLAND_NAMES[REEF_ISLANDS[i] as ReefIsland]} ends`, detail: 'Its term is over: the island is patrolled by Hawaiʻi\'s officers alone from tomorrow.' });
    });
  }
  if (civil.month === ECONOMY.grantDay.month && civil.day === ECONOMY.grantDay.day) {
    earn(ops, 'grant', ECONOMY.operatingGrantPerYear);
    events.push({ day: world.day, severity: 'good', title: 'The year\'s operating grant', detail: `${dollars(ECONOMY.operatingGrantPerYear)} for the State's fiscal year from ${dateLabel(today)}.` });
  }
  if (civil.day !== daysInMonth(civil.year, civil.month)) return;
  // The month's end: the staff paid, the boat kept, the tourism partners' fees.
  for (const m of ops.staff) pay(ops, m, today);
  // Each boat kept, the second for the days it was afloat in its first month (review M277's: a month's upkeep for its
  // last day).
  const afloat = ops.projects.find((p) => p.kind === 'boat' && p.readyDay <= today);
  const firstMonth = afloat ? Math.min(1, (today - afloat.readyDay + 1) / daysInMonth(civil.year, civil.month)) : 0;
  spend(ops, 'boat', Math.round(ECONOMY.boat.upkeepPerMonth * (1 + firstMonth)));
  const cover = knownCover(world, today);
  if (cover !== null && ops.tourismCover) earn(ops, 'tourism', Math.round(ECONOMY.tourismPerMonth * Math.min(1, cover / ops.tourismCover)));
  const net = LEDGER_LINES.reduce((n, l) => n + ops.month[l], 0);
  ops.months = [...ops.months, { year: civil.year, month: civil.month, lines: ops.month, funds: ops.funds }].slice(-ECONOMY.monthsKept);
  ops.month = emptyLedger();
  events.push({ day: world.day, severity: ops.funds < 0 ? 'critical' : 'info', title: `${MONTHS[civil.month - 1]}'s accounts`,
    detail: `${net >= 0 ? 'Up' : 'Down'} ${dollars(Math.abs(net))} over the month; ${dollars(ops.funds)} in hand.` });
  // In debt: the officers' hours the conservancy buys lapse, Hawaiʻi's own patrols alone again.
  if (ops.funds < 0 && s?.management && s.management.patrols.some((p) => p > 0)) {
    s.management.patrols = s.management.patrols.map(() => 0);
    s.management.patrolUntil = s.management.patrolUntil.map(() => null);
    events.push({ day: world.day, severity: 'critical', title: 'Patrols lapse', detail: 'The conservancy is in debt and can no longer pay for its officers\' hours: each island is patrolled by Hawaiʻi\'s officers alone.' });
  }
}

/** NOAA Coral Reef Watch's first alert's Degree Heating Weeks ("Alert Level 1", `heatStress.ts`): the heat's choice is asked from it (M274). */
export const HEAT_ALERT_DHW = 4;

/** Record the conservancy's choice in the heat: its plan kept, the heat's toll left to the next survey (M274); or why not. */
export function keepPlanInHeat(world: World): string | null {
  const s = world.sanctuaries[0];
  if ((s?.heatStress.peakDhw ?? 0) < HEAT_ALERT_DHW) return 'the reef is not under heat stress';
  if (world.operations.heatChoice !== null) return 'the choice is made for this episode';
  world.operations.heatChoice = 'keep';
  return null;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** A job's phase ending today: a survey's counts taken and its crew home; a rule's consultation over, or the State's rule made. */
function finishPhase(world: World, job: Job, today: number, events: GameEvent[]): void {
  const ops = world.operations;
  const s = world.sanctuaries[0];
  const phase = job.phaseEnds.indexOf(today);
  if (phase < 0) return;
  const last = phase === job.phaseEnds.length - 1;
  // A liaison's month setting up an island's volunteer monitoring over: its volunteers count from tomorrow (M277).
  if (job.kind === 'community' && job.island) {
    ops.community[REEF_ISLANDS.indexOf(job.island)] = today;
    ops.jobs = ops.jobs.filter((j) => j !== job);
    events.push({ day: world.day, severity: 'good', title: `Volunteers monitor ${ISLAND_NAMES[job.island]}'s reef`, detail: `Trained with its community, they count its grazing fish on ${ECONOMY.community.sitesPerMonth} transects a month, told at each month's end; ${dollars(ECONOMY.community.costPerYear)} a year.` });
    return;
  }
  // All of Maui Nui's (M327): each island not yet counting from tomorrow.
  if (job.kind === 'community') {
    const started = REEF_ISLANDS.filter((_, i) => ops.community[i] === null);
    ops.community = ops.community.map((d) => d ?? today);
    ops.jobs = ops.jobs.filter((j) => j !== job);
    events.push({ day: world.day, severity: 'good', title: 'Volunteers monitor Maui Nui\'s reef', detail: `Trained with ${started.length === REEF_ISLANDS.length ? 'each island\'s community' : `${started.map((i) => ISLAND_NAMES[i]).join(' and ')}'s communities`}, they count the grazing fish on ${ECONOMY.community.sitesPerMonth} transects a month on each island, told at each month's end; ${dollars(ECONOMY.community.costPerYear * started.length)} a year.` });
    return;
  }
  // A shore cleanup home (M307): what it cleared taken off its island's shore, told with what still lies there; no
  // count, so no data report's research grant.
  if (job.kind === 'survey' && job.survey === 'shoreCleanup' && job.island) {
    const tonnes = clearShore(world, job.island, job.days ?? 0);
    const title = `${SURVEY_NAMES.shoreCleanup} finished`;
    events.push({ day: world.day, severity: 'good', title, detail: `The ${boatName(job.boat)} is back after ${job.days} days off ${ISLAND_NAMES[job.island]}: ${tonnes.toFixed(1)} t of nets and debris removed from its shore.` });
    retire(ops, job, today, title, tonnes);
    return;
  }
  if (job.kind === 'survey' && job.survey) {
    const biologists = job.staff.filter((id) => ops.staff.find((m) => m.id === id)?.role === 'biologist').length;
    // Its areas' counts (M274): its sites laid over the island it went to, or over every island.
    const kind = AREA_KIND[job.survey];
    const areas = kind ? areaCounts(world, kind, job.island ? [job.island] : REEF_ISLANDS, (job.days ?? 0) * teamsOf(job.survey, biologists) * ECONOMY.reefSitesPerDay, STREAM.operations, job.id) : [];
    ops.areaCounts = keptCounts([...ops.areaCounts, ...areas]);
    const keep = (id: string, r: SurveyResult): void => {
      const counts = (world.monitoring[id] ??= []);
      // A program counts once a day (review M238's: a survey home on NOAA's own day made the save refuse to load): on a
      // day NOAA counted too, the count with the smaller error is kept (a count without error the exact one).
      const same = counts.at(-1)?.day === r.day ? counts.at(-1) : undefined;
      if (!same) counts.push(r);
      else if (same.cv !== null && (r.cv === null || r.cv < same.cv)) counts[counts.length - 1] = r;
    };
    // The sanctuary's own counts from a survey over the whole of it only: an island's tells its island. A benthic
    // survey's reef-wide cover is its strata's (`reefWideOf`), so the two never disagree; a census's each program's.
    const scale = job.island === null ? surveyCvScale(job.survey, job.days ?? 0, biologists, s) : null;
    const reef = s ? reefOf(s) : null;
    const wide = job.survey === 'reefBenthic' && reef ? reefWideOf(reef, areas) : null;
    const counted = job.island !== null ? [] : job.survey === 'reefBenthic' ? (wide ? (keep('coral', { day: today, value: wide.value, cv: wide.cv }), ['coral']) : [])
      : scale === null ? [] : SURVEY_PROGRAMS[job.survey].flatMap((id) => {
        const r = commissionedCount(world, id, scale, job.id);
        if (!r) return [];
        keep(id, r);
        return [id];
      });
    earn(ops, 'research', ECONOMY.researchGrantPerSurvey);
    const title = `${SURVEY_NAMES[job.survey]} finished`;
    const told = [counted.length > 0 ? `${counted.length} ${counted.length === 1 ? 'count' : 'counts'} for the monitoring` : '', areas.length > 0 ? `${areas.length === 1 ? `${ISLAND_NAMES[(areas[0] as AreaCount).island]}'s` : `${areas.length} islands'`} ${kind === 'cover' ? 'coral' : kind === 'herbivores' ? 'herbivores' : 'bleaching'} counted` : ''].filter(Boolean).join('; ');
    events.push({ day: world.day, severity: 'good', title, detail: `The ${boatName(job.boat)} is back after ${job.days} days at sea${job.island ? ` off ${ISLAND_NAMES[job.island]}` : ''}: ${told || 'nothing counted'}, and its data report's research grant, ${dollars(ECONOMY.researchGrantPerSurvey)}.` });
    retire(ops, job, today, title);
    return;
  }
  if (!job.rule) return;
  if (!last) {
    // The consultation done, its liaison free: the State takes the proposal.
    job.staff = [];
    events.push({ day: world.day, severity: 'info', title: 'Consultation finished', detail: `${ruleWords(job.rule)}: the communities heard, the proposal goes to the State's rule-making, about ${Math.round(ECONOMY.rule.stateDays / 30)} months.` });
    return;
  }
  const m = s?.management;
  // A rule already in force when the State's step ends changes nothing and is no rule made (review 29: it logged "A rule
  // takes effect" and counted in the review's community).
  if (!m || !s || !ruleChanges(s, job.rule)) {
    ops.jobs = ops.jobs.filter((j) => j !== job);
    events.push({ day: world.day, severity: 'info', title: 'A proposal closed', detail: `${ruleWords(job.rule)}: already in force, so the State made no new rule.` });
    return;
  }
  if (job.rule.type === 'protect_herbivores') {
    const rule = job.rule;
    if (rule.island === 'all') m.herbivoreProtection = m.herbivoreProtection.map(() => rule.protect);
    else m.herbivoreProtection[REEF_ISLANDS.indexOf(rule.island)] = rule.protect;
  }
  else m.limits[job.rule.group][job.rule.limit] = job.rule.option;
  const title = `${ruleWords(job.rule)}: in force`;
  events.push({ day: world.day, severity: 'good', title: 'A rule takes effect', detail: `${ruleWords(job.rule)} is made by the State and in force from today.` });
  retire(ops, job, today, title);
}

function retire(ops: Operations, job: Job, today: number, title: string, tonnes?: number): void {
  ops.jobs = ops.jobs.filter((j) => j !== job);
  const done: FinishedJob = { id: job.id, kind: job.kind as FinishedJob['kind'], endDay: today, title, startDay: job.startDay, waited: job.startDay - job.plannedDay, island: job.island };
  // A cleanup's tonnes alone (M307): a survey's and a rule's record as before.
  if (tonnes !== undefined) done.tonnes = tonnes;
  const all = [...ops.finished, done];
  // By definition a leap year's 366 days, so a year back is never a day short.
  ops.finished = all.filter((f, i) => i >= all.length - ECONOMY.monthsKept || f.endDay > today - 366);
}

/**
 * A world's operations as a current game would have run them from its first day to its day, with the events they
 * would have logged and its management after them (a debt's lapsed patrols): for a save from before them (v40 and
 * older, M238), which could do no work, hire no one and propose no rule, so its operations are its days' salaries, the
 * boat's upkeep, its patrols at their saved levels, the grant and the tourism fees on the coral's counts it holds.
 */
export function replayedOperations(world: World): { operations: Operations; campaign: Campaign; events: GameEvent[]; management: Sanctuary['management'][] } {
  const shadow: World = { ...world, day: 0, sanctuaries: world.sanctuaries.map((s) => ({ ...s, management: s.management ? structuredClone(s.management) : null })) };
  shadow.operations = openingOperations(shadow);
  // The campaign too (M239), stepped after the operations as the tick steps it: such a world's objectives are read from
  // nothing done, its review held on its first year's last day as a current game's is.
  shadow.campaign = openingCampaign(shadow);
  const events: GameEvent[] = [];
  for (let d = 1; d <= world.day; d++) {
    shadow.day = d;
    stepOperations(shadow, events);
    stepCampaign(shadow, events);
  }
  return { operations: shadow.operations, campaign: shadow.campaign, events, management: shadow.sanctuaries.map((s) => s.management) };
}

/**
 * The calendar's mean month, days: a year's rate as a month's (by definition a common year's twelfth, the accounts'
 * month; every page's "a month" reads it since review 33, M298, where the island pages had used 30 days).
 */
export const MONTH_DAYS = 365 / 12;

/** The running costs a month as the conservancy is committed today, each line and their sum, US dollars (M289). */
export interface Recurring { salaries: number; boats: number; patrols: number; volunteers: number; total: number }

/**
 * The costs that come back every month whatever is planned, as committed today (M289, the playtest's runway point: the
 * last month's spending had held its surveys and fuel, one-time work, so a month with two surveys read the funds as
 * five months where the month before had read twelve): the staff's pay, each boat's upkeep, the officers' hours the
 * patrols buy (a year's hours, `patrolCost`), and the volunteers' programs. Surveys, fuel, a rule's consultation and a
 * boat bought are one-time, paid when sent, and kept apart.
 */
export function recurringPerMonth(world: World): Recurring {
  const ops = world.operations;
  const salaries = ops.staff.reduce((n, m) => n + ECONOMY.monthlyPay[m.role], 0);
  const boats = ECONOMY.boat.upkeepPerMonth * ops.boats;
  const patrols = patrolCost(world.sanctuaries[0], MONTH_DAYS);
  // A year's cost over its twelve months (by definition).
  const volunteers = Math.round(ops.community.filter((d) => d !== null).length * ECONOMY.community.costPerYear / 12);
  return { salaries, boats, patrols, volunteers, total: salaries + boats + patrols + volunteers };
}

/**
 * The months of running costs the funds hold: the funds over the costs that recur each month (`recurringPerMonth`);
 * Infinity where nothing recurs. One reading for Manage's funds, its Finances indicator and the annual review (review
 * 29: three thresholds and two formulas had read one figure three ways).
 */
export function monthsInHand(world: World): number {
  const spent = recurringPerMonth(world).total;
  return spent > 0 ? world.operations.funds / spent : Infinity;
}

/** The one-time spending of a closed month, US dollars (M289): its surveys with their fuel, its rules' consultations, boats bought. */
export interface OneTime { surveys: number; consultations: number; capital: number; total: number }

/**
 * The last closed month's one-time spending: the surveys' field costs and the boat's fuel, the consultations of the
 * rules proposed in it (each its fixed cost, `proposeRule`: booked to the projects line beside the volunteers'
 * programs, so counted from the rules themselves), and boats bought.
 */
export function oneTimeLastMonth(world: World): OneTime {
  const ops = world.operations;
  const last = ops.months.at(-1);
  if (!last) return { surveys: 0, consultations: 0, capital: 0, total: 0 };
  const from = epochDayFromCivil(last.year, last.month, 1);
  const to = from + daysInMonth(last.year, last.month) - 1;
  const proposed = [...ops.jobs.filter((j) => j.kind === 'rule').map((j) => j.startDay), ...ops.finished.filter((f) => f.kind === 'rule').map((f) => f.startDay)]
    .filter((d) => d !== null && d >= from && d <= to).length;
  const surveys = Math.max(0, -(last.lines.surveys + last.lines.fuel));
  const consultations = Math.min(proposed * ECONOMY.rule.consultationCost, Math.max(0, -last.lines.projects));
  const capital = Math.max(0, -last.lines.capital);
  return { surveys, consultations, capital, total: surveys + consultations + capital };
}

/** The funds' standing: good with six months of running costs or more, fair with two, poor below or in debt (JUDGED: half a year's margin, and the two months a grant's late payment takes to matter). */
export const FUNDS_MONTHS = { good: 6, fair: 2 } as const;
export function fundsLevel(world: World): 'good' | 'fair' | 'poor' {
  const months = monthsInHand(world);
  return world.operations.funds < 0 || months < FUNDS_MONTHS.fair ? 'poor' : months < FUNDS_MONTHS.good ? 'fair' : 'good';
}
